<template>
  <!--
    Teleport to body so that v-window's CSS transforms don't break
    position:fixed on mobile tab switching.
  -->
  <Teleport to="body">
    <!-- Standalone back button — positioned symmetrically on the left -->
    <Transition name="back-btn">
      <div v-if="showStandaloneBack" key="back" class="back-button-container" :style="backButtonStyle"
        :class="{ 'qa-hidden': !qaStore.visible, 'is-light': isLight }">
        <div class="back-button-pill">
          <button class="back-button-standalone" @click="onBackClick">
            <v-icon :size="20">mdi-chevron-left</v-icon>
          </button>
        </div>
      </div>
    </Transition>

    <div class="quick-actions-container" :style="containerStyle"
      :class="{ 'qa-hidden': !qaStore.visible, 'is-light': isLight }">
      <!-- Login page: only theme toggle -->
      <div v-if="isLoginPage" class="combined-buttons qa-pill-animate">
        <button class="action-button left-button" @click="toggleTheme">
          <v-icon :size="20">{{ isLight ? 'mdi-weather-night' : 'mdi-white-balance-sunny' }}</v-icon>
        </button>
      </div>

      <!-- Not authenticated (non-login pages): theme toggle + filter + search + login button -->
      <div v-else-if="!userStore.isAuthenticated" class="combined-buttons qa-pill-animate">
        <button class="action-button left-button" @click="toggleTheme">
          <v-icon :size="20">{{ isLight ? 'mdi-weather-night' : 'mdi-white-balance-sunny' }}</v-icon>
        </button>
        <Transition name="qa-slot">
          <div v-if="qaStore.showFilter" class="qa-slot">
            <div class="button-divider" />
            <button class="action-button middle-button" :class="{ 'filter-active': qaStore.filterActive }"
              @click="handleFilter">
              <v-icon :size="20">mdi-filter-variant</v-icon>
              <span v-if="qaStore.filterActive" class="filter-badge" />
            </button>
          </div>
        </Transition>
        <Transition name="qa-slot">
          <div v-if="qaStore.showSearch" class="qa-slot">
            <div class="button-divider" />
            <QuickActionSearch :placeholder="qaStore.searchPlaceholder" toggle-icon="mdi-magnify" no-dropdown
              @search="handleSearch" @close="handleSearchClose" />
          </div>
        </Transition>
        <div class="button-divider" />
        <button class="login-btn" @click="onAvatarClick">
          <span class="login-label">{{ $t('nav.login') }}</span>
          <v-avatar size="32" color="primary">
            <v-icon size="20" color="white">mdi-login</v-icon>
          </v-avatar>
        </button>
      </div>

      <!-- Combined Button Group (authenticated only) -->
      <div v-else class="combined-buttons qa-pill-animate" :class="{ 'avatar-only': isAvatarOnly }">
        <!-- League 3-dot menu (league sub-routes only) -->
        <Transition name="qa-slot">
          <div v-if="isSubRouteWithBar" class="qa-slot">
            <button ref="secondaryBtnRef" class="action-button left-button"
              :class="{ active: activeMenu === 'secondary' }" @click="toggleSecondaryMenu">
              <v-icon :size="20">mdi-dots-vertical</v-icon>
            </button>
            <div class="button-divider" />
          </div>
        </Transition>

        <!-- Edit article button (article detail route, admins only) -->
        <Transition name="qa-slot">
          <div v-if="isArticleDetailRoute && canEditArticle" class="qa-slot">
            <button class="action-button left-button" @click="openEditArticleWindow">
              <v-icon :size="22">mdi-pencil</v-icon>
            </button>
            <div class="button-divider" />
          </div>
        </Transition>

        <!-- Mail article button (article detail route, admins only) -->
        <Transition name="qa-slot">
          <div v-if="isArticleDetailRoute && canEditArticle" class="qa-slot">
            <button class="action-button left-button" @click="openArticleMailingModal">
              <v-icon :size="21">mdi-email-outline</v-icon>
            </button>
            <div class="button-divider" />
          </div>
        </Transition>

        <!-- Create/plus button (sub-routes, e.g. league schedule) -->
        <Transition name="qa-slot">
          <div v-if="qaStore.showCreate" class="qa-slot">
            <button ref="createBtnRef" class="action-button left-button" :class="{ active: activeMenu === 'create' }"
              @click="onCreateClick">
              <v-icon :size="24">{{ qaStore.createIcon }}</v-icon>
            </button>
            <div class="button-divider" />
          </div>
        </Transition>

        <Transition name="qa-slot">
          <div v-if="isArticleListRoute && canCreateArticle" class="qa-slot">
            <button class="action-button left-button" @click="openNewArticleWindow">
              <v-icon :size="24">mdi-plus</v-icon>
            </button>
            <div class="button-divider" />
          </div>
        </Transition>

        <!-- Second create button (e.g. treasury cash-edit) -->
        <Transition name="qa-slot">
          <div v-if="qaStore.showCreate2" class="qa-slot">
            <button class="action-button left-button" @click="qaStore.onCreate2?.($event)">
              <v-icon :size="24">{{ qaStore.createIcon2 }}</v-icon>
            </button>
            <div class="button-divider" />
          </div>
        </Transition>

        <!-- Primary (left) Button (normal routes only, hidden when back) -->
        <Transition name="qa-slot">
          <div v-if="!isSubRouteWithBar && !qaStore.hidePrimary && !isPrimaryBack" class="qa-slot">
            <button class="action-button left-button" :class="{ active: activeMenu === 'primary' }"
              @click="onPrimaryClick">
              <v-icon :size="24">{{ qaStore.primaryIcon }}</v-icon>
            </button>
            <div class="button-divider" />
          </div>
        </Transition>

        <!-- Filter (middle) Button -->
        <Transition name="qa-slot">
          <div v-if="qaStore.showFilter" class="qa-slot">
            <button class="action-button middle-button" :class="{ 'filter-active': qaStore.filterActive }"
              @click="handleFilter">
              <v-icon :size="20">mdi-filter-variant</v-icon>
              <span v-if="qaStore.filterActive" class="filter-badge" />
            </button>
            <div class="button-divider" />
          </div>
        </Transition>

        <!-- Search (normal routes only) -->
        <Transition name="qa-slot">
          <div v-if="!isLeagueSubRoute && qaStore.showSearch" class="qa-slot">
            <QuickActionSearch :placeholder="qaStore.searchPlaceholder" toggle-icon="mdi-magnify" no-dropdown
              @search="handleSearch" @close="handleSearchClose" />
            <div class="button-divider" />
          </div>
        </Transition>

        <!-- Highlights button (next to avatar) -->
        <Transition name="qa-slot">
          <div v-if="qaStore.showHighlights && !isUserProfileRoute" class="qa-slot">
            <button class="action-button left-button highlights-button" @click="qaStore.onHighlights?.($event)">
              <v-icon :size="20">{{ qaStore.highlightsIcon }}</v-icon>
            </button>
            <div class="button-divider" />
          </div>
        </Transition>

        <!-- Minimize chat button -->
        <Transition name="qa-slot">
          <div v-if="qaStore.showMinimize" class="qa-slot">
            <button class="action-button middle-button" @click="onMinimizeClick">
              <v-icon :size="20">mdi-window-minimize</v-icon>
            </button>
            <div class="button-divider" />
          </div>
        </Transition>

        <!-- Secondary (middle) Button (normal routes only) -->
        <Transition name="qa-slot">
          <div v-if="!isSubRouteWithBar && qaStore.secondaryItems.length > 0" class="qa-slot">
            <button ref="secondaryBtnRef" class="action-button middle-button"
              :class="{ active: activeMenu === 'secondary' }" @click="toggleSecondaryMenu">
              <v-icon :size="20">{{ qaStore.secondaryIcon }}</v-icon>
            </button>
            <div class="button-divider" />
          </div>
        </Transition>

        <!-- Calendar sync button -->
        <Transition name="qa-slot">
          <div v-if="qaStore.showCalendar && qaStore.calendarItems.length > 0" class="qa-slot">
            <button ref="calendarBtnRef" class="action-button middle-button"
              :class="{ active: activeMenu === 'calendar' }" @click="toggleCalendarMenu">
              <v-icon :size="20">{{ qaStore.calendarIcon }}</v-icon>
            </button>
            <div class="button-divider" />
          </div>
        </Transition>

        <!-- Share button (next to avatar) -->
        <Transition name="qa-slot">
          <div v-if="qaStore.showShare" class="qa-slot">
            <button class="action-button middle-button share-button" @click="qaStore.onShare?.($event)">
              <v-icon :size="20">{{ qaStore.shareIcon }}</v-icon>
            </button>
            <div class="button-divider" />
          </div>
        </Transition>

        <!-- Save button (e.g. article editor) -->
        <Transition name="qa-slot">
          <div v-if="qaStore.showSave" class="qa-slot">
            <button class="action-button middle-button" :disabled="qaStore.saveLoading || qaStore.saveDisabled"
              @click="qaStore.onSave?.($event)">
              <v-progress-circular v-if="qaStore.saveLoading" indeterminate size="18" width="2" />
              <v-icon v-else :size="20">{{ qaStore.saveIcon }}</v-icon>
            </button>
            <div class="button-divider" />
          </div>
        </Transition>

        <!-- Delete button (e.g. article editor) -->
        <Transition name="qa-slot">
          <div v-if="qaStore.showDelete" class="qa-slot">
            <button class="action-button middle-button qa-delete-button" @click="qaStore.onDelete?.($event)">
              <v-icon :size="20" color="error">{{ qaStore.deleteIcon }}</v-icon>
            </button>
            <div class="button-divider" />
          </div>
        </Transition>

        <!-- More (right) Button — Avatar -->
        <button ref="moreBtnRef" class="action-button right-button avatar-button"
          :class="{ active: activeMenu === 'more' }" @click="onAvatarClick">
          <v-badge v-if="chatStore.totalUnreadCount > 0" dot color="error" offset-x="2" offset-y="2"
            class="chat-unread-badge">
            <v-avatar size="32" color="primary">
              <v-img v-if="qaStore.avatarSrc" :src="qaStore.avatarSrc" cover />
              <span v-else class="avatar-initials">{{ qaStore.avatarInitials }}</span>
            </v-avatar>
          </v-badge>
          <v-avatar v-else size="32" color="primary">
            <v-img v-if="qaStore.avatarSrc" :src="qaStore.avatarSrc" cover />
            <span v-else class="avatar-initials">{{ qaStore.avatarInitials }}</span>
          </v-avatar>
        </button>
      </div>

      <!-- Overlay + Context Menus (already in body via this outer Teleport) -->
      <div v-if="activeMenu" class="quick-overlay" @click="close" />

      <!-- Context Menu (More) -->
      <transition name="quick-menu">
        <div v-if="activeMenu === 'more'" class="quick-context-menu nav-context-menu" :style="menuPosition">
          <!-- User card -->
          <div class="nav-user-card">
            <div class="nav-user-row">
              <div class="nav-user-avatar">
                <v-img v-if="qaStore.avatarSrc" :src="qaStore.avatarSrc" cover />
                <span v-else>{{ qaStore.avatarInitials }}</span>
              </div>
              <div class="nav-user-info">
                <div class="nav-user-name">{{ userStore.getUserName }}</div>
                <div class="nav-user-meta">{{ userStore.user?.email }}</div>
              </div>
              <div v-if="isAdmin" class="nav-user-badge">{{ $t('admin') }}</div>
            </div>
            <!-- Quick action buttons -->
            <div class="nav-quick-actions">
              <button class="nav-quick-btn accent" @click="onItemClick({ icon: '', label: '', action: 'createEvent' })">
                <v-icon size="18">mdi-plus</v-icon>
                <span>{{ $t('nav.newEvent') }}</span>
              </button>
              <button class="nav-quick-btn" @click="onItemClick({ icon: '', label: '', action: 'messages' })">
                <v-icon size="18">mdi-send</v-icon>
                <span>{{ $t('nav.messages') }}</span>
                <span v-if="chatStore.totalUnreadCount > 0" class="nav-badge">{{ chatStore.totalUnreadCount }}</span>
              </button>
              <button class="nav-quick-btn" @click="onItemClick({ icon: '', label: '', action: 'profile' })">
                <v-icon size="18">mdi-account</v-icon>
                <span>{{ $t('nav.profile') }}</span>
              </button>
            </div>
          </div>

          <!-- Main navigation -->
          <div class="nav-main-routes">
            <button class="nav-route-btn" :class="{ active: route.path === '/' }" @click="onNavItemClick('/')">
              <span>{{ $t('nav.events') }}</span>
            </button>
            <button class="nav-route-btn" :class="{ active: route.path === '/teams' }"
              @click="onNavItemClick('/teams')">
              <span>{{ $t('nav.teams') }}</span>
            </button>
            <button class="nav-route-btn" :class="{ active: route.path === '/leagues' }"
              @click="onNavItemClick('/leagues')">
              <span>{{ $t('nav.organizations') }}</span>
            </button>
          </div>

          <!-- Nav items scroll area -->
          <div class="nav-scroll-wrapper">
            <div class="nav-scroll-fade nav-scroll-fade--top" :class="{ visible: navScrollCanUp }" />
            <div ref="navScrollRef" class="nav-scroll" @scroll="onNavScroll">
              <!-- Dynamic items from props -->
              <template v-if="navMainItems.length > 0">
                <div v-for="item in navMainItems" :key="item.action" class="nav-item" @click="onItemClick(item)">
                  <div class="nav-item-icon"><v-icon size="16">{{ item.icon }}</v-icon></div>
                  {{ item.label }}
                </div>
              </template>

              <div class="nav-item" @click="onItemClick({ icon: '', label: '', action: 'account' })">
                <div class="nav-item-icon"><v-icon size="16">mdi-cog-outline</v-icon></div>
                {{ $t('nav.accountSettings') }}
              </div>
              <div class="nav-item" @click="onItemClick({ icon: '', label: '', action: 'players' })">
                <div class="nav-item-icon"><v-icon size="16">mdi-account-group</v-icon></div>
                {{ $t('nav.myPlayers') }}
              </div>
              <div class="nav-item" @click="onItemClick({ icon: '', label: '', action: 'dependants' })">
                <div class="nav-item-icon"><v-icon size="16">mdi-human-male-child</v-icon></div>
                {{ $t('nav.dependants') }}
              </div>
              <!-- Language picker -->
              <div class="nav-item nav-item-lang" @click.stop="showLangPicker = !showLangPicker">
                <div class="nav-item-icon"><v-icon size="16">mdi-translate</v-icon></div>
                {{ currentLangLabel }}
                <v-icon size="14" class="ml-auto" style="opacity:.5">{{ showLangPicker ? 'mdi-chevron-up' :
                  'mdi-chevron-down'
                  }}</v-icon>
              </div>
              <Transition name="lang-expand">
                <div v-if="showLangPicker" class="lang-options">
                  <button v-for="lang in langOptions" :key="lang.code" class="lang-option"
                    :class="{ active: lang.code === currentLocale }" @click.stop="switchLocale(lang.code)">
                    <span class="lang-flag">{{ lang.flag }}</span>
                    <span>{{ lang.label }}</span>
                    <v-icon v-if="lang.code === currentLocale" size="14" class="ml-auto">mdi-check</v-icon>
                  </button>
                </div>
              </Transition>
              <div class="nav-item nav-item-toggle" @click="toggleTheme">
                <div class="nav-item-icon"><v-icon size="16">{{ isLight ? 'mdi-weather-night' :
                    'mdi-white-balance-sunny' }}</v-icon>
                </div>
                {{ isLight ? $t('nav.darkMode') : $t('nav.lightMode') }}
                <div class="nav-toggle" :class="{ on: !isLight }">
                  <div class="nav-toggle-knob" />
                </div>
              </div>
            </div>
            <div class="nav-scroll-fade nav-scroll-fade--bottom" :class="{ visible: navScrollCanDown }" />
          </div>

          <!-- Footer -->
          <div class="nav-footer">
            <button class="nav-footer-btn" @click="onItemClick({ icon: '', label: '', action: 'contact' })">
              <v-icon size="16">mdi-email-outline</v-icon>
              {{ $t('nav.contact') }}
            </button>
            <button class="nav-footer-btn logout" @click="onItemClick({ icon: '', label: '', action: 'logout' })">
              <v-icon size="16">mdi-logout</v-icon>
              {{ $t('nav.logout') }}
            </button>
          </div>
          <div class="nav-version">build {{ buildVersion }}</div>
        </div>
      </transition>

      <!-- Context Menu (Secondary) -->
      <transition name="quick-menu">
        <div v-if="activeMenu === 'secondary'" class="quick-context-menu" :style="secondaryMenuPosition">
          <div class="quick-context-menu-content">
            <template v-for="(item, i) in effectiveSecondaryItems" :key="'s' + i">
              <!-- Section header -->
              <div v-if="item.type === 'header'" class="quick-menu-header">
                {{ item.label }}
              </div>
              <!-- Regular item -->
              <div v-else class="quick-menu-item" :class="{
                'quick-menu-item-active': (isLeagueSubRoute || isTeamSubRoute) && route.path === item.action.replace('leagueNav:', ''),
                'quick-menu-item--disabled': item.action === 'report' && !userStore.isAuthenticated,
              }" @click="onItemClick(item)">
                <div class="quick-menu-item-text">
                  <span>{{ item.label }}</span>
                  <span v-if="item.action === 'report' && !userStore.isAuthenticated" class="quick-menu-item-sub">{{
                    t('report.loginRequired') }}</span>
                </div>
                <v-icon size="18">{{ item.icon }}</v-icon>
              </div>
              <div
                v-if="item.type !== 'header' && effectiveSecondaryItems[i + 1] && effectiveSecondaryItems[i + 1].type !== 'header' && i < effectiveSecondaryItems.length - 1"
                class="quick-menu-divider" />
            </template>
          </div>
        </div>
      </transition>

      <!-- Context Menu (Team Pencil) -->
      <transition name="quick-menu">
        <div v-if="activeMenu === 'teamPencil'" class="quick-context-menu" :style="teamPencilMenuPosition">
          <div class="quick-context-menu-content">
            <template v-for="(item, i) in teamPencilItems" :key="'tp' + i">
              <div class="quick-menu-item" @click="onItemClick(item)">
                <span>{{ item.label }}</span>
                <v-icon size="18">{{ item.icon }}</v-icon>
              </div>
              <div v-if="i < teamPencilItems.length - 1" class="quick-menu-divider" />
            </template>
          </div>
        </div>
      </transition>

      <!-- Context Menu (Create/plus) -->
      <transition name="quick-menu">
        <div v-if="activeMenu === 'create'" class="quick-context-menu" :style="createMenuPosition">
          <div class="quick-context-menu-content">
            <template v-for="(item, i) in qaStore.createItems" :key="'cr' + i">
              <div class="quick-menu-item" @click="onItemClick(item)">
                <span>{{ item.label }}</span>
                <v-icon size="18">{{ item.icon }}</v-icon>
              </div>
              <div v-if="i < qaStore.createItems.length - 1" class="quick-menu-divider" />
            </template>
          </div>
        </div>
      </transition>

      <!-- Context Menu (Calendar sync) -->
      <transition name="quick-menu">
        <div v-if="activeMenu === 'calendar'" class="quick-context-menu" :style="calendarMenuPosition">
          <div class="quick-context-menu-content">
            <template v-for="(item, i) in qaStore.calendarItems" :key="'cal' + i">
              <div class="quick-menu-item" @click="onItemClick(item)">
                <span>{{ item.label }}</span>
                <v-icon size="18">{{ item.icon }}</v-icon>
              </div>
              <div v-if="i < qaStore.calendarItems.length - 1" class="quick-menu-divider" />
            </template>
          </div>
        </div>
      </transition>
    </div>
  </Teleport>

  <!-- League Edit Modal -->
  <LeagueFormModal v-model="showLeagueFormModal" :leagueId="leagueMatch?.id ?? academyMatch?.id ?? ''"
    @saved="onLeagueSaved" />

  <!-- Team Edit Modal -->
  <TeamFormModal v-model="showTeamFormModal" :teamId="teamMatch?.id ?? ''" @saved="onTeamSaved" />

  <!-- Team Members Modal -->
  <TeamMembersModal v-model="showMembersModal" :teamId="teamMatch?.id ?? ''" />

  <!-- Price List Dialog -->
  <PriceListDialog v-model="showPriceListModal" :teamId="teamMatch?.id ?? ''" />

  <!-- Global Create Event (styled bottom sheet fallback) -->
  <EventFormModal v-model="showGlobalEventFormModal" :initialDate="globalEventInitialDate" @saved="() => { }" />

  <!-- Report Modal -->
  <ReportModal v-model="showReportModal" :override-target="reportOverrideTarget" />

  <!-- Article Mailing Modal -->
  <ArticleMailingModal v-model="showArticleMailingModal" :url-title="articleDetailMatch?.urlTitle ?? null" />

  <!-- Block User Modal -->
  <BlockUserModal v-model="showBlockModal" :target="blockTarget" @blocked="blockedSnackbar = true" />

  <v-snackbar v-model="blockedSnackbar" location="top" color="black" timeout="2500" :style="{ zIndex: 99999 }">
    <v-icon class="me-2">mdi-account-cancel-outline</v-icon>{{ $t('block.successText') }}
  </v-snackbar>

  <!-- Report user picker (messages route) -->
  <BottomSheetModal v-model="showReportPicker" hide-confirm @close="showReportPicker = false">
    <template #title>
      <div class="modal-title-container">
        <span>{{ t('report.pickUserTitle') }}</span>
        <span class="modal-subtitle">{{ t('report.pickUserSubtitle') }}</span>
      </div>
    </template>
    <RoleSection :users="reportPickerUsers" :title="t('chatMembers.members')" :empty-label="t('chatMembers.noMembers')"
      :editable="false" show-chevron @click-user="onReportPickerSelect" />
  </BottomSheetModal>

  <!-- Block user picker (messages route — group chats) -->
  <BottomSheetModal v-model="showBlockPicker" hide-confirm @close="showBlockPicker = false">
    <template #title>
      <div class="modal-title-container">
        <span>{{ t('block.pickUserTitle') }}</span>
        <span class="modal-subtitle">{{ t('block.pickUserSubtitle') }}</span>
      </div>
    </template>
    <RoleSection :users="blockPickerUsers" :title="t('chatMembers.members')" :empty-label="t('chatMembers.noMembers')"
      :editable="false" show-chevron @click-user="onBlockPickerSelect" />
  </BottomSheetModal>

  <!-- Simulate Matches Confirmation -->
  <CenteredModal v-model="showSimulateMatchesConfirm" :showCloseButton="true" maxWidth="360px" :zIndex="10001">
    <div class="delete-confirm-dialog">
      <div class="delete-confirm-icon">
        <v-icon size="48" color="info">mdi-play-box-outline</v-icon>
      </div>
      <h3 class="delete-confirm-title">{{ $t('nav.simulateMatches') }}</h3>
      <p class="delete-confirm-subtitle">{{ $t('nav.simulateMatchesConfirm') }}</p>
      <div class="delete-confirm-input-wrap">
        <input v-model="simulateMatchesInput" class="delete-confirm-input"
          :placeholder="$t('typeToConfirm', { word: $t('simulateWord') })" autocomplete="off" />
      </div>
      <div class="delete-confirm-actions">
        <button class="delete-confirm-btn delete-confirm-btn--cancel" @click="cancelSimulateMatches">{{ $t('cancel')
          }}</button>
        <button class="delete-confirm-btn delete-confirm-btn--confirm"
          :disabled="!isSimulateMatchesValid || simulatingMatches" @click="executeSimulateMatches">
          <template v-if="simulatingMatches">
            <v-progress-circular size="16" width="2" indeterminate style="margin-right: 4px;" />
          </template>
          {{ $t('nav.simulateMatchesRun') }}
        </button>
      </div>
    </div>
  </CenteredModal>

  <!-- Close Regular Season Confirmation -->
  <CenteredModal v-model="showCloseRegularConfirm" :showCloseButton="true" maxWidth="360px" :zIndex="10001">
    <div class="delete-confirm-dialog">
      <div class="delete-confirm-icon">
        <v-icon size="48" color="warning">mdi-lock-check-outline</v-icon>
      </div>
      <h3 class="delete-confirm-title">{{ $t('nav.closeRegular') }}</h3>
      <p class="delete-confirm-subtitle">{{ $t('nav.closeRegularConfirm') }}</p>
      <div class="delete-confirm-input-wrap">
        <input v-model="closeRegularInput" class="delete-confirm-input"
          :placeholder="$t('typeToConfirm', { word: $t('closeRegularWord') })" autocomplete="off" />
      </div>
      <div class="delete-confirm-actions">
        <button class="delete-confirm-btn delete-confirm-btn--cancel" @click="cancelCloseRegular">{{ $t('cancel')
          }}</button>
        <button class="delete-confirm-btn delete-confirm-btn--confirm"
          :disabled="!isCloseRegularValid || closingRegular" @click="executeCloseRegular">
          <template v-if="closingRegular">
            <v-progress-circular size="16" width="2" indeterminate style="margin-right: 4px;" />
          </template>
          {{ $t('nav.closeRegularRun') }}
        </button>
      </div>
    </div>
  </CenteredModal>

  <!-- Conflict report after closing regular season -->
  <CenteredModal v-model="showConflictsDialog" :showCloseButton="true" maxWidth="400px" :zIndex="10002">
    <div class="delete-confirm-dialog">
      <div class="delete-confirm-icon">
        <v-icon size="48" color="warning">mdi-alert-outline</v-icon>
      </div>
      <h3 class="delete-confirm-title">{{ $t('nav.closeRegularConflictsTitle') }}</h3>
      <p class="delete-confirm-subtitle" style="text-align: left;">{{ $t('nav.closeRegularConflicts') }}</p>
      <div class="conflicts-list">
        <div v-for="(c, i) in conflictsList" :key="i" class="conflict-item">
          <span class="conflict-position">{{ c.position }}</span>
          <span class="conflict-old">{{ c.oldTeamName || c.oldTeamId }}</span>
          <v-icon size="16" class="conflict-arrow">mdi-arrow-right</v-icon>
          <span class="conflict-new">{{ c.newTeamName || c.newTeamId }}</span>
        </div>
      </div>
      <div class="delete-confirm-actions" style="justify-content: center;">
        <button class="delete-confirm-btn delete-confirm-btn--confirm" @click="showConflictsDialog = false">
          {{ $t('nav.closeRegularConflictsAck') }}
        </button>
      </div>
    </div>
  </CenteredModal>

  <!-- Close Playoff Groups Confirmation -->
  <CenteredModal v-model="showClosePlayoffGroupsConfirm" :showCloseButton="true" maxWidth="360px" :zIndex="10001">
    <div class="delete-confirm-dialog">
      <div class="delete-confirm-icon">
        <v-icon size="48" color="warning">mdi-lock-open-variant-outline</v-icon>
      </div>
      <h3 class="delete-confirm-title">{{ $t('nav.closePlayoffGroups') }}</h3>
      <p class="delete-confirm-subtitle">{{ $t('nav.closePlayoffGroupsConfirm') }}</p>
      <div class="delete-confirm-input-wrap">
        <input v-model="closePlayoffGroupsInput" class="delete-confirm-input"
          :placeholder="$t('typeToConfirm', { word: $t('closePlayoffGroupsWord') })" autocomplete="off" />
      </div>
      <div class="delete-confirm-actions">
        <button class="delete-confirm-btn delete-confirm-btn--cancel" @click="cancelClosePlayoffGroups">{{ $t('cancel')
          }}</button>
        <button class="delete-confirm-btn delete-confirm-btn--confirm"
          :disabled="!isClosePlayoffGroupsValid || closingPlayoffGroups" @click="executeClosePlayoffGroups">
          <template v-if="closingPlayoffGroups">
            <v-progress-circular size="16" width="2" indeterminate style="margin-right: 4px;" />
          </template>
          {{ $t('nav.closePlayoffGroupsRun') }}
        </button>
      </div>
    </div>
  </CenteredModal>

  <!-- Override Warning Dialog -->
  <CenteredModal v-model="showOverrideWarning" :showCloseButton="true" maxWidth="420px" :zIndex="10002">
    <div class="delete-confirm-dialog">
      <div class="delete-confirm-icon">
        <v-icon size="48" color="warning">mdi-alert-outline</v-icon>
      </div>
      <h3 class="delete-confirm-title">{{ $t('nav.overrideWarningTitle') }}</h3>
      <p class="delete-confirm-subtitle">{{ $t('nav.overrideWarningSubtitle') }}</p>
      <div class="override-list">
        <div v-for="(item, i) in overrideWarningItems" :key="i" class="override-item">
          <span class="override-location">{{ item.location }}</span>
          <span class="override-change">
            <strong>{{ item.oldTeamName }}</strong>
            <v-icon size="16" class="override-arrow">mdi-arrow-right</v-icon>
            <strong>{{ item.newTeamName }}</strong>
          </span>
        </div>
      </div>
      <div class="delete-confirm-actions">
        <button class="delete-confirm-btn delete-confirm-btn--cancel" @click="cancelOverrides">{{ $t('cancel')
          }}</button>
        <button class="delete-confirm-btn delete-confirm-btn--confirm" @click="acknowledgeOverrides">
          <template v-if="closingPlayoffGroups">
            <v-progress-circular size="16" width="2" indeterminate style="margin-right: 4px;" />
          </template>
          {{ $t('nav.overrideAcknowledge') }}
        </button>
      </div>
    </div>
  </CenteredModal>

  <!-- Leave Team Confirmation -->
  <CenteredModal v-model="showLeaveTeamConfirm" :showCloseButton="true" maxWidth="380px" :zIndex="10001">
    <div class="delete-confirm-dialog">
      <div class="delete-confirm-icon">
        <v-icon size="48" color="error">mdi-exit-run</v-icon>
      </div>
      <h3 class="delete-confirm-title">{{ $t('team.leaveTeam') }}</h3>
      <p class="delete-confirm-subtitle">{{ $t('team.leaveTeamConfirm', { team: teamFullData?.name || '' }) }}</p>
      <p class="delete-confirm-subtitle" style="margin-top: 8px; opacity: .8;">{{ $t('team.leaveTeamChatNote') }}</p>
      <div class="delete-confirm-actions">
        <button class="delete-confirm-btn delete-confirm-btn--cancel" :disabled="leavingTeam"
          @click="showLeaveTeamConfirm = false">{{ $t('cancel') }}</button>
        <button class="delete-confirm-btn delete-confirm-btn--confirm" :disabled="leavingTeam"
          @click="executeLeaveTeam">
          <template v-if="leavingTeam">
            <v-progress-circular size="16" width="2" indeterminate style="margin-right: 4px;" />
          </template>
          {{ $t('team.leaveTeamConfirmBtn') }}
        </button>
      </div>
    </div>
  </CenteredModal>

  <!-- Leave Group Confirmation -->
  <CenteredModal v-model="showLeaveGroupConfirm" :showCloseButton="true" maxWidth="380px" :zIndex="10001">
    <div class="delete-confirm-dialog">
      <div class="delete-confirm-icon">
        <v-icon size="48" color="error">mdi-exit-run</v-icon>
      </div>
      <h3 class="delete-confirm-title">{{ $t('chatMembers.leaveGroup') }}</h3>
      <p class="delete-confirm-subtitle">{{ $t('chatMembers.leaveGroupConfirm') }}</p>
      <div class="delete-confirm-actions">
        <button class="delete-confirm-btn delete-confirm-btn--cancel" :disabled="leavingGroup"
          @click="showLeaveGroupConfirm = false">{{ $t('cancel') }}</button>
        <button class="delete-confirm-btn delete-confirm-btn--confirm" :disabled="leavingGroup"
          @click="executeLeaveGroup">
          <template v-if="leavingGroup">
            <v-progress-circular size="16" width="2" indeterminate style="margin-right: 4px;" />
          </template>
          {{ $t('chatMembers.leaveGroupConfirmBtn') }}
        </button>
      </div>
    </div>
  </CenteredModal>
</template>

<script setup lang="ts">
import { ref, nextTick, computed, onMounted, onUnmounted, watch } from 'vue'
import { useDisplay, useTheme } from 'vuetify'
import { useRouter, useRoute } from 'vue-router'
import { useAppStore } from '@/stores/app/appStore'
import { useUserStore } from '@/stores/user/userStore'
import { useChatStore } from '@/stores/chat/chatStore'
import { useNavTransition } from '@/composables/useNavTransition'
import { useQuickActionsStore, type QuickActionItem } from '@/stores/quickActions/quickActionsStore'
import QuickActionSearch from '@/components/QuickActionSearch.vue'
import LeagueFormModal from '@/components/league/LeagueFormModal.vue'
import TeamFormModal from '@/components/Team/TeamFormModal.vue'
import TeamMembersModal from '@/components/Team/TeamMembersModal.vue'
import PriceListDialog from '@/components/Team/PriceListDialog.vue'
import EventFormModal from '@/components/EventFormModal.vue'
import CenteredModal from '@/components/CenteredModal.vue'
import ReportModal from '@/components/ReportModal.vue'
import ArticleMailingModal from '@/components/Articles/ArticleMailingModal.vue'
import BlockUserModal from '@/components/BlockUserModal.vue'
import BottomSheetModal from '@/components/BottomSheetModal.vue'
import RoleSection from '@/components/Team/RoleSection.vue'
import { getUserAvatarUrl } from '@/utils/imageUtils'
import type { UserLite } from '@/components/Team/teamRoles'
import { setLocale, getCurrentLocale, type AppLocale } from '@/plugins/i18n'
import { useI18n } from 'vue-i18n'
import axiosInstance from '@/services/axiosInstance'
import { BUILD_VERSION as buildVersion } from '@/version'

// Re-export for backward compat
export type { QuickActionItem }

const { t } = useI18n({ useScope: 'global' })
const qaStore = useQuickActionsStore()
const appStore = useAppStore()
const userStore = useUserStore()
const chatStore = useChatStore()
const router = useRouter()
const route = useRoute()
const { isMainRoute } = useNavTransition()

// Admin detection
const isAdmin = computed(() => {
  const roles = (userStore.user as any)?.roles
  return Array.isArray(roles) && roles.includes('administrator')
})

// Filter items that are already shown as hardcoded nav items
const navMainItems = computed(() => {
  const hardcodedActions = new Set([
    'messages', 'profile', 'account', 'players', 'dependants',
    'createInvitation', 'bugReport', 'toggleTheme', 'logout', 'createEvent'
  ])
  return qaStore.items.filter(item => !hardcodedActions.has(item.action))
})

function onNavItemClick(path: string) {
  close()
  router.push(path)
}

const isLoginPage = computed(() => route.path === '/login')
const canGoBack = computed(() => window.history.length > 1)

/** Whether the primary button is a back/chevron-left action */
const isPrimaryBack = computed(() => qaStore.primaryIcon === 'mdi-chevron-left')

/** League sub-route detection: /leagues/:id/:season/... (excludes academy) */
const leagueMatch = computed(() => {
  if (route.path.match(/^\/leagues\/[^/]+\/academy/)) return null
  const m = route.path.match(/^\/leagues\/([^/]+)\/([^/]+)/)
  if (!m) return null
  return { id: m[1], season: m[2] }
})
const isLeagueSubRoute = computed(() => !!leagueMatch.value)

/** Academy sub-route detection: /leagues/:id/academy */
const academyMatch = computed(() => {
  const m = route.path.match(/^\/leagues\/([^/]+)\/academy/)
  if (!m) return null
  return { id: m[1] }
})
const isAcademySubRoute = computed(() => !!academyMatch.value)

const isArticleListRoute = computed(() => {
  return !!route.path.match(/^\/leagues\/[^/]+\/(?:articles|academy\/articles|[^/]+\/articles)$/)
})

const articleDetailMatch = computed(() => {
  const p = route.path
  let m = p.match(/^\/leagues\/([^/]+)\/academy\/articles\/([^/]+)$/)
  if (m) return { leagueId: m[1], season: null as string | null, urlTitle: m[2], isAcademy: true }
  m = p.match(/^\/leagues\/([^/]+)\/([^/]+)\/articles\/([^/]+)$/)
  if (m) return { leagueId: m[1], season: m[2], urlTitle: m[3], isAcademy: false }
  return null
})
const isArticleDetailRoute = computed(() => !!articleDetailMatch.value)

const canEditArticle = computed(() => {
  const roles = userStore.user?.userRoles || []
  if (roles.includes('administrator')) return true
  return userStore.hasRole(leagueAdmins.value)
})

const canCreateArticle = computed(() => {
  const roles = userStore.user?.userRoles || []
  if (roles.includes('administrator')) return true
  return userStore.hasRole(leagueAdmins.value)
})

/** Team sub-route detection: /teams/:id/... */
const teamMatch = computed(() => {
  const m = route.path.match(/^\/teams\/([^/]+)/)
  if (!m) return null
  return { id: m[1] }
})
const isTeamSubRoute = computed(() => !!teamMatch.value)

const teamAdmins = ref<any[]>([])
const teamFullData = ref<any>(null)
const isTeamAdmin = computed(() => {
  const roles = userStore.user?.userRoles || []
  if (roles.includes('administrator') || roles.includes('coach')) return true
  return userStore.hasRole(teamAdmins.value)
})

const isTeamMember = computed(() => {
  const team = teamFullData.value
  const userId = userStore.user?._id
  if (!team || !userId) return false
  const checkArray = (arr: any[]) => arr?.some((item: any) =>
    typeof item === 'string' ? item === userId : item._id === userId
  ) || false
  return checkArray(team.players) || checkArray(team.goalkeepers) ||
    checkArray(team.coaches) || checkArray(team.treasurers) ||
    checkArray(team.captains) || checkArray(team.externalOrganizers)
})

const showTeamFormModal = ref(false)
const showMembersModal = ref(false)
const showPriceListModal = ref(false)
const showGlobalEventFormModal = ref(false)
const globalEventInitialDate = ref<Date | null>(null)
const showReportModal = ref(false)
const reportOverrideTarget = ref<any>(null)
const showArticleMailingModal = ref(false)
const showReportPicker = ref(false)
const reportPickerUsers = ref<UserLite[]>([])

// Block user picker (used in /messages/:id route)
const showBlockPicker = ref(false)
const blockPickerUsers = ref<UserLite[]>([])

// Block user modal
const showBlockModal = ref(false)
const blockTarget = ref<{ id: string; name: string; image?: string; initials?: string } | null>(null)
const blockedSnackbar = ref(false)

function extractUserIdFromRoute(): string | null {
  const m = route.path.match(/^\/users\/([0-9a-fA-F]{24})/)
  return m?.[1] ?? null
}

// Track block relation for the user currently visible on /users/:id
const profileRelation = ref<{ iBlocked: boolean; theyBlockedMe: boolean } | null>(null)
const isProfileBlockedRelation = computed(() => !!(profileRelation.value && (profileRelation.value.iBlocked || profileRelation.value.theyBlockedMe)))

async function fetchProfileRelation() {
  const id = extractUserIdFromRoute()
  if (!id || id === userStore.user?._id) {
    profileRelation.value = null
    return
  }
  try {
    const { data } = await axiosInstance.get(`${API_BASE_URL}/users/${id}`)
    profileRelation.value = data?.relation || null
  } catch {
    profileRelation.value = null
  }
}

watch(() => route.path, () => {
  if (extractUserIdFromRoute()) fetchProfileRelation()
  else profileRelation.value = null
}, { immediate: true })

async function openBlockModalForCurrentRoute() {
  const id = extractUserIdFromRoute()
  if (!id) return
  // Try to fetch minimal user info for the preview header
  try {
    const { data } = await axiosInstance.get(`${API_BASE_URL}/users/${id}`)
    const u = data?.user || data || {}
    const fullName = [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.nickName || u.name || 'Uživatel'
    const initials = ((u.firstName?.[0] || '') + (u.lastName?.[0] || '')).toUpperCase() || '?'
    blockTarget.value = {
      id,
      name: fullName,
      image: getUserAvatarUrl(u) || undefined,
      initials,
    }
  } catch {
    blockTarget.value = { id, name: 'Uživatel', initials: '?' }
  }
  showBlockModal.value = true
}

function openReportPickerForMessages() {
  const conv = chatStore.activeConversation
  if (!conv) return
  const myId = userStore.user?._id
  reportPickerUsers.value = conv.members
    .filter(m => {
      const uid = typeof m.user === 'object' ? m.user._id : (m.user as any)
      return uid && uid !== myId
    })
    .map(m => {
      const u: any = m.user
      return {
        _id: u._id,
        firstName: u.firstName,
        lastName: u.lastName,
        name: u.name,
        nickName: u.nickName,
        avatar: u.avatar,
      } as UserLite
    })
  reportOverrideTarget.value = null
  showReportPicker.value = true
}

function onReportPickerSelect(user: UserLite) {
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || user.nickName || user.name || 'Uživatel'
  const initials = ((user.firstName?.[0] || '') + (user.lastName?.[0] || '')).toUpperCase() || '?'
  reportOverrideTarget.value = {
    kind: 'user',
    kindLabel: t('report.targets.user'),
    ref: user._id,
    name: fullName,
    image: getUserAvatarUrl(user) || undefined,
    initials,
  }
  showReportPicker.value = false
  // Open after picker closes to avoid sheet conflict
  nextTick(() => {
    showReportModal.value = true
  })
}

/** Other-than-me members of the active conversation (used for block picker / direct shortcut). */
function getOtherConversationMembers(): UserLite[] {
  const conv = chatStore.activeConversation
  if (!conv) return []
  const myId = userStore.user?._id
  return conv.members
    .filter(m => {
      const uid = typeof m.user === 'object' ? (m.user as any)._id : (m.user as any)
      return uid && uid !== myId
    })
    .map(m => {
      const u: any = m.user
      return {
        _id: u._id,
        firstName: u.firstName,
        lastName: u.lastName,
        name: u.name,
        nickName: u.nickName,
        avatar: u.avatar,
      } as UserLite
    })
}

/** Whether to show "Block user" item in the messages /:id "More" submenu. */
const canBlockFromMessages = computed(() => {
  const conv = chatStore.activeConversation
  if (!conv) return false
  // Block makes sense for direct/group conversations with at least one other member
  return getOtherConversationMembers().length > 0
})

const canLeaveMessagesGroup = computed(() => {
  const conv = chatStore.activeConversation
  const userId = userStore.user?._id
  if (!conv || !userId || conv.type !== 'group') return false
  return conv.members.some(m => {
    const uid = typeof m.user === 'object' ? (m.user as any)._id : (m.user as any)
    return uid === userId
  })
})

function openBlockFromMessages() {
  const others = getOtherConversationMembers()
  if (others.length === 0) return
  const conv = chatStore.activeConversation
  // Direct chat → open block modal directly for the other user
  if (conv?.type === 'direct' && others.length === 1) {
    openBlockModalForUser(others[0])
    return
  }
  // Group / team chat → show picker
  blockPickerUsers.value = others
  showBlockPicker.value = true
}

function openBlockModalForUser(user: UserLite) {
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || user.nickName || user.name || 'Uživatel'
  const initials = ((user.firstName?.[0] || '') + (user.lastName?.[0] || '')).toUpperCase() || '?'
  blockTarget.value = {
    id: user._id,
    name: fullName,
    image: getUserAvatarUrl(user) || undefined,
    initials,
  }
  showBlockModal.value = true
}

function onBlockPickerSelect(user: UserLite) {
  showBlockPicker.value = false
  nextTick(() => {
    openBlockModalForUser(user)
  })
}

const onTeamSaved = (data: any) => {
  if (data?.primaryColor || data?.secondaryColor) {
    window.dispatchEvent(new CustomEvent('team-colors-updated', {
      detail: { primaryTeamColor: data.primaryColor, secondaryTeamColor: data.secondaryColor }
    }))
  }
}

const fetchTeamData = async (teamId: string) => {
  try {
    const { data } = await axiosInstance.get(`${API_BASE_URL}/teams/${teamId}`)
    teamAdmins.value = data.admins || []
    teamFullData.value = data
  } catch {
    teamAdmins.value = []
    teamFullData.value = null
  }
}

watch(() => teamMatch.value?.id, (id) => {
  if (id) fetchTeamData(id)
  else { teamAdmins.value = []; teamFullData.value = null }
}, { immediate: true })

/** Gamecenter sub-route detection: /gamecenter/:id */
const isGamecenterSubRoute = computed(() => /^\/gamecenter\/[^/]+/.test(route.path))

/** Event sub-route detection: /event/:id, /events/:id, /eventdatalog/:id */
const isEventSubRoute = computed(() => /^\/(events?|eventdatalog)\/[^/]+/.test(route.path))

/** Messages conversation route: /messages/:id */
const isMessagesConvRoute = computed(() => /^\/messages\/[^/]+/.test(route.path))

/** User profile route: /users/:id (id may be `<24-hex>-<slug>`) */
const isUserProfileRoute = computed(() => /^\/users\/[0-9a-fA-F]{24}/.test(route.path))

/** Combined: is a sub-route with its own bar (league or team or academy) */
const isSubRouteWithBar = computed(() => isLeagueSubRoute.value || isAcademySubRoute.value || isTeamSubRoute.value || isGamecenterSubRoute.value || isEventSubRoute.value || isMessagesConvRoute.value || isUserProfileRoute.value)

/** Sub-routes that render a fixed top bar (offsetting QA pill position).
 *  User profile route shows the 3-dot menu but has no fixed bar. */
const hasFixedSubBar = computed(() => isLeagueSubRoute.value || isAcademySubRoute.value || isTeamSubRoute.value || isGamecenterSubRoute.value)

/** League admin check for treasury visibility */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'
const leagueAdmins = ref<string[]>([])
const isLeagueAdmin = computed(() => {
  const roles = userStore.user?.userRoles || []
  if (roles.includes('administrator') || roles.includes('coach')) return true
  return userStore.hasRole(leagueAdmins.value)
})

const showLeagueFormModal = ref(false)
const showSimulateMatchesConfirm = ref(false)
const simulatingMatches = ref(false)
const simulateMatchesInput = ref('')

// Leave team confirmation state
const showLeaveTeamConfirm = ref(false)
const leavingTeam = ref(false)

// Leave group confirmation state
const showLeaveGroupConfirm = ref(false)
const leavingGroup = ref(false)

async function executeLeaveTeam() {
  const teamId = teamMatch.value?.id
  if (!teamId || leavingTeam.value) return
  leavingTeam.value = true
  try {
    await axiosInstance.post(`${API_BASE_URL}/teams/${teamId}/leave`)
    // Refresh chat conversations to drop team chat
    try { await chatStore.fetchConversations() } catch { /* noop */ }
    // Refresh user's teams
    try { await userStore.fetchUserData?.() } catch { /* noop */ }
    showLeaveTeamConfirm.value = false
    router.push('/teams')
  } catch (err: any) {
    console.error('[leaveTeam] failed:', err?.response?.data || err)
  } finally {
    leavingTeam.value = false
  }
}

async function executeLeaveGroup() {
  const conv = chatStore.activeConversation
  const userId = userStore.user?._id
  if (!conv?._id || !userId || leavingGroup.value) return
  leavingGroup.value = true
  try {
    await chatStore.removeMember(conv._id, userId)
    chatStore.conversations = chatStore.conversations.filter(c => c._id !== conv._id)
    chatStore.clearActiveConversation()
    showLeaveGroupConfirm.value = false
    router.replace('/messages')
  } catch (err: any) {
    console.error('[leaveGroup] failed:', err?.response?.data || err)
  } finally {
    leavingGroup.value = false
  }
}

const isSimulateMatchesValid = computed(() =>
  simulateMatchesInput.value.toLowerCase() === t('simulateWord').toLowerCase()
)

const cancelSimulateMatches = () => {
  showSimulateMatchesConfirm.value = false
  simulateMatchesInput.value = ''
}

const executeSimulateMatches = async () => {
  if (!isSimulateMatchesValid.value || !leagueMatch.value?.season) return
  simulatingMatches.value = true
  try {
    await axiosInstance.post(`${API_BASE_URL}/leagues/${leagueMatch.value.id}/season/${leagueMatch.value.season}/simulate-matches`)
    showSimulateMatchesConfirm.value = false
    simulateMatchesInput.value = ''
    appStore.triggerRefetch()
  } catch (e: any) {
    const msg = e?.response?.data?.error || 'Chyba při simulaci zápasů'
    alert(msg)
    console.error('Chyba při simulaci zápasů:', e)
  } finally {
    simulatingMatches.value = false
  }
}

// Close regular season
const showCloseRegularConfirm = ref(false)
const showConflictsDialog = ref(false)
const conflictsList = ref<any[]>([])
const closingRegular = ref(false)
const closeRegularInput = ref('')

const isCloseRegularValid = computed(() =>
  closeRegularInput.value.toLowerCase() === t('closeRegularWord').toLowerCase()
)

const cancelCloseRegular = () => {
  showCloseRegularConfirm.value = false
  closeRegularInput.value = ''
}

const executeCloseRegular = async () => {
  if (!isCloseRegularValid.value || !leagueMatch.value?.season) return
  closingRegular.value = true
  try {
    const { data } = await axiosInstance.post(`${API_BASE_URL}/leagues/${leagueMatch.value.id}/season/${leagueMatch.value.season}/close-regular`)
    showCloseRegularConfirm.value = false
    closeRegularInput.value = ''
    appStore.triggerRefetch()

    // Show conflict report if any pre-assigned teams were replaced
    const conflicts = data?.conflicts || []
    if (conflicts.length > 0) {
      conflictsList.value = conflicts
      showConflictsDialog.value = true
    }
  } catch (e: any) {
    const msg = e?.response?.data?.error || 'Chyba při uzavírání základní části'
    alert(msg)
    console.error('Chyba při uzavírání základní části:', e)
  } finally {
    closingRegular.value = false
  }
}

/* Close Playoff Groups */
const seasonScenarioType = ref('')
const showClosePlayoffGroupsConfirm = ref(false)
const closingPlayoffGroups = ref(false)
const closePlayoffGroupsInput = ref('')

// Override warning
const showOverrideWarning = ref(false)
const overrideWarningItems = ref<{ oldTeamName: string; newTeamName: string; location: string }[]>([])

const hasPlayoffGroups = computed(() =>
  ['groups_playoff', 'prelim_groups_playoff', 'mh_groups_playoff'].includes(seasonScenarioType.value)
)

const isClosePlayoffGroupsValid = computed(() =>
  closePlayoffGroupsInput.value.toLowerCase() === t('closePlayoffGroupsWord').toLowerCase()
)

const cancelClosePlayoffGroups = () => {
  showClosePlayoffGroupsConfirm.value = false
  closePlayoffGroupsInput.value = ''
}

const doClosePlayoffGroups = async () => {
  try {
    await axiosInstance.post(`${API_BASE_URL}/leagues/${leagueMatch.value!.id}/season/${leagueMatch.value!.season}/close-playoff-groups`)
    showClosePlayoffGroupsConfirm.value = false
    closePlayoffGroupsInput.value = ''
    appStore.triggerRefetch()
  } catch (e: any) {
    const msg = e?.response?.data?.error || 'Chyba při uzavírání playoff skupin'
    alert(msg)
    console.error('Chyba při uzavírání playoff skupin:', e)
  } finally {
    closingPlayoffGroups.value = false
  }
}

const executeClosePlayoffGroups = async () => {
  if (!isClosePlayoffGroupsValid.value || !leagueMatch.value?.season) return
  closingPlayoffGroups.value = true
  try {
    // Check for overrides first
    const { data } = await axiosInstance.get(
      `${API_BASE_URL}/leagues/${leagueMatch.value.id}/season/${leagueMatch.value.season}/close-playoff-groups-preview`
    )
    if (data.hasOverrides) {
      const items: { oldTeamName: string; newTeamName: string; location: string }[] = []
      for (const o of data.bracket || []) {
        items.push({
          oldTeamName: o.oldTeamName,
          newTeamName: o.newTeamName,
          location: `${t('nav.overrideBracket')}: ${o.slot} (${o.side === 'home' ? t('nav.overrideHome') : t('nav.overrideAway')})`,
        })
      }
      for (const o of data.consolation || []) {
        items.push({
          oldTeamName: o.oldTeamName,
          newTeamName: o.newTeamName,
          location: `${t('nav.overrideConsolation')}: slot ${o.slotIndex + 1}`,
        })
      }
      overrideWarningItems.value = items
      showOverrideWarning.value = true
      return
    }
    // No overrides — proceed directly
    await doClosePlayoffGroups()
  } catch (e: any) {
    const msg = e?.response?.data?.error || 'Chyba při uzavírání playoff skupin'
    alert(msg)
    console.error('Chyba při uzavírání playoff skupin:', e)
    closingPlayoffGroups.value = false
  }
}

const acknowledgeOverrides = async () => {
  showOverrideWarning.value = false
  await doClosePlayoffGroups()
}

const cancelOverrides = () => {
  showOverrideWarning.value = false
  closingPlayoffGroups.value = false
}

const onLeagueSaved = (data: any) => {
  if (data?.primaryColor || data?.secondaryColor) {
    window.dispatchEvent(new CustomEvent('league-colors-updated', {
      detail: { primaryColor: data.primaryColor, secondaryColor: data.secondaryColor }
    }))
  }
}

const fetchLeagueAdmins = async (urlTitle: string) => {
  try {
    const { data } = await axiosInstance.get(`${API_BASE_URL}/leagues/${urlTitle}`)
    leagueAdmins.value = data.admins || []
  } catch {
    leagueAdmins.value = []
  }
}

watch(() => leagueMatch.value?.id ?? academyMatch.value?.id, (id) => {
  if (id) fetchLeagueAdmins(id)
  else leagueAdmins.value = []
}, { immediate: true })

watch(() => [leagueMatch.value?.id, leagueMatch.value?.season] as const, ([id, seasonYear]) => {
  if (id && seasonYear) fetchSeasonScenario(id, seasonYear)
  else seasonScenarioType.value = ''
}, { immediate: true })

const fetchSeasonScenario = async (urlTitle: string, year: string) => {
  try {
    const { data } = await axiosInstance.get(`${API_BASE_URL}/leagues/seasonlist/${urlTitle}`)
    const season = Array.isArray(data) ? data.find((s: any) => s.year === year) : null
    seasonScenarioType.value = season?.playoffConfig?.scenarioType || ''
  } catch {
    seasonScenarioType.value = ''
  }
}

/** League navigation items for the 3-dot menu */
const leagueNavItems = computed<QuickActionItem[]>(() => {
  if (!leagueMatch.value) return []
  const items: QuickActionItem[] = [
    { icon: 'mdi-calendar-text', label: t('nav.schedule'), action: `leagueNav:/leagues/${leagueMatch.value.id}/${leagueMatch.value.season}/schedule` },
    { icon: 'mdi-table', label: t('nav.standings'), action: `leagueNav:/leagues/${leagueMatch.value.id}/${leagueMatch.value.season}/standings` },
    { icon: 'mdi-chart-bar', label: t('nav.statistics'), action: `leagueNav:/leagues/${leagueMatch.value.id}/${leagueMatch.value.season}/statistics` },
    { icon: 'mdi-tournament', label: t('playoff.title'), action: `leagueNav:/leagues/${leagueMatch.value.id}/${leagueMatch.value.season}/playoff` },
    { icon: 'mdi-newspaper', label: t('nav.articles'), action: `leagueNav:/leagues/${leagueMatch.value.id}/${leagueMatch.value.season}/articles` },

  ]
  if (isLeagueAdmin.value) {
    items.push({ icon: 'mdi-cash-register', label: t('nav.treasury'), action: `leagueNav:/leagues/${leagueMatch.value.id}/${leagueMatch.value.season}/treasury` })
    items.push({ icon: '', label: 'Admin', action: '', type: 'header' })
    items.push({ icon: 'mdi-cog-outline', label: t('nav.leagueSettings'), action: 'leagueSettings' })
    items.push({ icon: 'mdi-play-box-outline', label: t('nav.simulateMatches'), action: 'simulateMatches' })
    items.push({ icon: 'mdi-lock-check-outline', label: t('nav.closeRegular'), action: 'closeRegularSeason' })
    if (hasPlayoffGroups.value) {
      items.push({ icon: 'mdi-lock-open-variant-outline', label: t('nav.closePlayoffGroups'), action: 'closePlayoffGroups' })
    }
  }
  return items
})

/** Team navigation items for the 3-dot menu */
const teamNavItems = computed<QuickActionItem[]>(() => {
  if (!teamMatch.value) return []
  const items: QuickActionItem[] = [
    { icon: 'mdi-calendar-text', label: t('nav.schedule'), action: `leagueNav:/teams/${teamMatch.value.id}/schedule` },
    { icon: 'mdi-account-group', label: t('nav.members'), action: `leagueNav:/teams/${teamMatch.value.id}/members` },
    { icon: 'mdi-cash-register', label: t('nav.treasury'), action: `leagueNav:/teams/${teamMatch.value.id}/treasury` },
  ]
  if (isTeamAdmin.value) {
    items.push({ icon: '', label: 'Admin', action: '', type: 'header' })
    items.push({ icon: 'mdi-cog-outline', label: t('nav.teamSettings'), action: 'teamSettings' })
  }
  return items
})

/** Can current user leave this team? Member of any role and not the last admin. */
const canLeaveTeam = computed(() => {
  if (!isTeamMember.value && !isTeamAdmin.value) return false
  const team = teamFullData.value
  const userId = userStore.user?._id
  if (!team || !userId) return false
  const isAdminMember = (team.admins || []).some((u: any) =>
    typeof u === 'string' ? u === userId : u?._id === userId
  )
  if (isAdminMember && (team.admins?.length || 0) <= 1) return false
  return true
})

/** Academy navigation items for the 3-dot menu */
const academyNavItems = computed<QuickActionItem[]>(() => {
  if (!academyMatch.value) return []
  const items: QuickActionItem[] = [
    { icon: 'mdi-calendar-text', label: t('nav.schedule'), action: `leagueNav:/leagues/${academyMatch.value.id}/academy/schedule` },
    { icon: 'mdi-newspaper', label: t('nav.articles'), action: `leagueNav:/leagues/${academyMatch.value.id}/academy/articles` },
  ]
  if (isLeagueAdmin.value) {
    items.push({ icon: 'mdi-cash-register', label: t('treasury'), action: `leagueNav:/leagues/${academyMatch.value.id}/academy/treasury` })
    items.push({ icon: 'mdi-pencil', label: t('editLeague'), action: 'academyEditLeague' })
  }
  return items
})

/** Effective secondary items — league/team/academy nav on sub-routes, otherwise store.
 *  When `secondaryView === 'more'`, render the drill-down sub-menu (back + report). */
const effectiveSecondaryItems = computed<QuickActionItem[]>(() => {
  if (secondaryView.value === 'more') {
    if (isUserProfileRoute.value) {
      return [
        { icon: 'mdi-chevron-left', label: t('common.back'), action: 'submenu:back' },
        { icon: '', label: t('report.more'), action: '', type: 'header' },
        { icon: 'mdi-account-cancel-outline', label: t('block.title'), action: 'blockUser' },
        { icon: 'mdi-flag-outline', label: t('report.reportContent'), action: 'report' },
      ]
    }
    if (isTeamSubRoute.value && canLeaveTeam.value) {
      return [
        { icon: 'mdi-chevron-left', label: t('common.back'), action: 'submenu:back' },
        { icon: '', label: t('report.more'), action: '', type: 'header' },
        { icon: 'mdi-exit-run', label: t('team.leaveTeam'), action: 'leaveTeam' },
        { icon: 'mdi-flag-outline', label: t('report.reportContent'), action: 'report' },
      ]
    }
    if (isMessagesConvRoute.value) {
      const items: QuickActionItem[] = [
        { icon: 'mdi-chevron-left', label: t('common.back'), action: 'submenu:back' },
        { icon: '', label: t('report.more'), action: '', type: 'header' },
      ]
      if (canLeaveMessagesGroup.value) {
        items.push({ icon: 'mdi-exit-run', label: t('chatMembers.leaveGroup'), action: 'leaveGroup' })
      }
      if (canBlockFromMessages.value) {
        items.push({ icon: 'mdi-account-cancel-outline', label: t('block.title'), action: 'blockFromMessages' })
      }
      items.push({ icon: 'mdi-flag-outline', label: t('report.reportContent'), action: 'report' })
      return items
    }
    return [
      { icon: 'mdi-chevron-left', label: t('common.back'), action: 'submenu:back' },
      { icon: '', label: t('report.more'), action: '', type: 'header' },
      { icon: 'mdi-flag-outline', label: t('report.reportContent'), action: 'report' },
    ]
  }

  if (isUserProfileRoute.value) {
    const items: QuickActionItem[] = []
    // Send Message — only when page exposes it via highlights flag and not blocked
    if (qaStore.showHighlights && !isProfileBlockedRelation.value) {
      items.push({ icon: 'mdi-message-outline', label: t('sendMessage'), action: 'userSendMessage' })
    }
    // Edit profile — page-provided as secondaryItems[0]
    const editItem = qaStore.secondaryItems.find(it => it.action === 'editProfile')
    if (editItem) {
      items.push({ icon: 'mdi-pencil-outline', label: editItem.label, action: 'editProfile' })
    }
    items.push({ icon: 'mdi-chevron-right', label: t('report.more'), action: 'submenu:more' })
    return items
  }

  let base: QuickActionItem[] = []
  if (isAcademySubRoute.value) base = academyNavItems.value
  else if (isLeagueSubRoute.value) base = leagueNavItems.value
  else if (isTeamSubRoute.value) base = teamNavItems.value
  else base = qaStore.secondaryItems

  // Append "Další →" drill-down trigger only when a 3-dot menu is meaningfully shown
  if (isSubRouteWithBar.value) {
    return [
      ...base,
      { icon: 'mdi-chevron-right', label: t('report.more'), action: 'submenu:more' },
    ]
  }
  return base
})

/** Only avatar button visible in the pill (no other icons) */
const isAvatarOnly = computed(() => {
  if (isSubRouteWithBar.value) return false // league/team routes show 3-dot
  const hasPrimary = !qaStore.hidePrimary && !isPrimaryBack.value
  return !hasPrimary
    && !qaStore.showFilter
    && !qaStore.showSearch
    && qaStore.secondaryItems.length === 0
    && !qaStore.showHighlights
    && !qaStore.showShare
    && !qaStore.showSave
    && !qaStore.showDelete
    && !qaStore.showCalendar
    && !qaStore.showCreate
    && !qaStore.showCreate2
    && !qaStore.showMinimize
})

/** Show standalone back button on left side */
const showStandaloneBack = computed(() => {
  if (isSubRouteWithBar.value) return true
  if (isLoginPage.value) return canGoBack.value
  if (!userStore.isAuthenticated && !isMainRoute(route.path) && canGoBack.value) return true
  if (userStore.isAuthenticated && isPrimaryBack.value && !qaStore.hidePrimary) return true
  return false
})

const BANNER_HEIGHT = 76
const MENU_HEIGHT = 50
const LEAGUE_BAR_HEIGHT = 76
const CONTAINER_MAX_WIDTH = 900
const GAP = 10

const { mdAndUp } = useDisplay()
const theme = useTheme()
const isLight = computed(() => !theme.global.current.value.dark)
const windowWidth = ref(window.innerWidth)

let resizeRafId: number | null = null
function recomputeActiveMenuPosition() {
  switch (activeMenu.value) {
    case 'more':
      if (moreBtnRef.value) menuPosition.value = computeMenuPosition(moreBtnRef.value, { withMaxHeight: true })
      break
    case 'secondary':
      if (secondaryBtnRef.value) secondaryMenuPosition.value = computeMenuPosition(secondaryBtnRef.value)
      break
    case 'create':
      if (createBtnRef.value) createMenuPosition.value = computeMenuPosition(createBtnRef.value)
      break
    case 'calendar':
      if (calendarBtnRef.value) calendarMenuPosition.value = computeMenuPosition(calendarBtnRef.value)
      break
    default:
      break
  }
}

function onResize() {
  if (resizeRafId !== null) cancelAnimationFrame(resizeRafId)
  resizeRafId = requestAnimationFrame(() => {
    resizeRafId = null
    windowWidth.value = window.innerWidth
    recomputeActiveMenuPosition()
  })
}

function onOrientationChange() {
  // Orientation change on mobile may not fire `resize` consistently; re-run
  // the same logic across two frames to catch the post-rotation layout.
  onResize()
  setTimeout(onResize, 250)
}

// ── Callback proxies (call store callbacks instead of emitting) ──
function handleFilter() {
  qaStore.onFilter?.()
}

function handleSearch(query: string) {
  qaStore.onSearch?.(query)
}

function handleSearchClose() {
  qaStore.onSearchClose?.()
}

const activeMenu = ref<'primary' | 'more' | 'secondary' | 'teamPencil' | 'create' | 'calendar' | null>(null)
/** Drill-down view inside the secondary 3-dot context menu */
const secondaryView = ref<'main' | 'more'>('main')
const menuPosition = ref<Record<string, string>>({})
const secondaryMenuPosition = ref<Record<string, string>>({})
const teamPencilMenuPosition = ref<Record<string, string>>({})
const createMenuPosition = ref<Record<string, string>>({})
const calendarMenuPosition = ref<Record<string, string>>({})

/**
 * Compute a viewport-clamped position for a context menu anchored to a trigger button.
 * Right-aligns the menu to the trigger via CSS `right` (so it always hugs the button
 * regardless of menu width). Falls back to `left` only when the menu would overflow
 * the left edge of the viewport.
 */
function computeMenuPosition(
  btn: HTMLElement,
  opts: { maxMenuWidth?: number; margin?: number; gap?: number; withMaxHeight?: boolean } = {},
): Record<string, string> {
  const rect = btn.getBoundingClientRect()
  const screenWidth = window.innerWidth
  const margin = opts.margin ?? 8
  const gap = opts.gap ?? 8
  const maxMenuWidth = opts.maxMenuWidth ?? 260
  // Available width when right-aligned to the trigger button
  const availableWidth = rect.right - margin

  const style: Record<string, string> = {
    top: `${rect.bottom + gap}px`,
  }

  if (availableWidth >= Math.min(maxMenuWidth, 200)) {
    // Enough room — keep menu right-aligned to button (looks anchored)
    style.right = `${screenWidth - rect.right}px`
  } else {
    // Not enough room on the right side — fall back to left-anchored, clamped
    style.left = `${margin}px`
    style.maxWidth = `${screenWidth - margin * 2}px`
  }

  if (opts.withMaxHeight) {
    style.maxHeight = `calc(100dvh - ${rect.bottom + gap + 12}px)`
  }
  return style
}

const scrollY = ref(0)
const navScrollRef = ref<HTMLElement | null>(null)
const navScrollCanUp = ref(false)
const navScrollCanDown = ref(false)

function onNavScroll() {
  const el = navScrollRef.value
  if (!el) return
  navScrollCanUp.value = el.scrollTop > 4
  navScrollCanDown.value = el.scrollTop + el.clientHeight < el.scrollHeight - 4
}

const moreBtnRef = ref<HTMLElement | null>(null)
const secondaryBtnRef = ref<HTMLElement | null>(null)
const createBtnRef = ref<HTMLElement | null>(null)
const calendarBtnRef = ref<HTMLElement | null>(null)

function onScroll() {
  scrollY.value = window.scrollY
}

onMounted(() => {
  scrollY.value = window.scrollY
  windowWidth.value = window.innerWidth
  window.addEventListener('scroll', onScroll, { passive: true })
  window.addEventListener('resize', onResize, { passive: true })
  window.addEventListener('orientationchange', onOrientationChange, { passive: true })
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', onResize, { passive: true } as AddEventListenerOptions)
  }

  // Initialize chat socket listeners & fetch unread count globally
  if (userStore.isAuthenticated) {
    chatStore.initSocketListeners()
    chatStore.fetchUnreadCount()
  }
})

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll)
  window.removeEventListener('resize', onResize)
  window.removeEventListener('orientationchange', onOrientationChange)
  if (window.visualViewport) {
    window.visualViewport.removeEventListener('resize', onResize as EventListener)
  }
  if (resizeRafId !== null) {
    cancelAnimationFrame(resizeRafId)
    resizeRafId = null
  }
})

// Init chat when user authenticates after mount
watch(() => userStore.isAuthenticated, (isAuth) => {
  if (isAuth) {
    chatStore.initSocketListeners()
    chatStore.fetchUnreadCount()
  }
})

// Automatic detection of banner visibility from store
const hasBannerVisible = computed(() => {
  if (qaStore.hasBanner === false) return false
  if (!userStore.isAuthenticated) return false
  return appStore.tickerCount > 0
})

const containerStyle = computed(() => {
  let topPos: number
  const gap = GAP

  if (hasFixedSubBar.value) {
    topPos = Math.max(0, LEAGUE_BAR_HEIGHT - scrollY.value) + gap
  } else {
    const bannerRemaining = hasBannerVisible.value ? Math.max(0, BANNER_HEIGHT - scrollY.value) : 0
    const menuOffset = (mdAndUp.value && qaStore.hasMenuOffset !== false) ? MENU_HEIGHT : 0
    topPos = bannerRemaining + menuOffset + gap
  }

  const rightOffset = mdAndUp.value
    ? Math.max(10, (windowWidth.value - CONTAINER_MAX_WIDTH) / 2)
    : 10

  return {
    top: `calc(${topPos}px + env(safe-area-inset-top, 0px))`,
    right: `${rightOffset}px`,
  }
})

/** Symmetric left-side position for the standalone back button */
const backButtonStyle = computed(() => {
  let topPos: number
  const gap = GAP

  if (hasFixedSubBar.value) {
    topPos = Math.max(0, LEAGUE_BAR_HEIGHT - scrollY.value) + gap
  } else {
    const bannerRemaining = hasBannerVisible.value ? Math.max(0, BANNER_HEIGHT - scrollY.value) : 0
    const menuOffset = (mdAndUp.value && qaStore.hasMenuOffset !== false) ? MENU_HEIGHT : 0
    topPos = bannerRemaining + menuOffset + gap
  }

  const leftOffset = mdAndUp.value
    ? Math.max(10, (windowWidth.value - CONTAINER_MAX_WIDTH) / 2)
    : 10

  return {
    top: `calc(${topPos}px + env(safe-area-inset-top, 0px))`,
    left: `${leftOffset}px`,
  }
})

function onPrimaryClick() {
  close()
  qaStore.onPrimary?.()
}

function onMinimizeClick() {
  close()
  qaStore.onMinimize?.()
}

/** Back button click */
function getArticleListPath() {
  const path = route.path
  if (!path.includes('/articles/')) return null
  if (path.startsWith('/articles/')) return '/articles'
  const index = path.indexOf('/articles/')
  if (index === -1) return null
  return path.slice(0, index + '/articles'.length)
}

function onBackClick() {
  if (userStore.isAuthenticated && isPrimaryBack.value) {
    onPrimaryClick()
    return
  }

  if (window.history.length > 1) {
    router.back()
    return
  }

  const fallback = getArticleListPath()
  if (fallback) {
    router.push(fallback)
  } else {
    router.back()
  }
}

function onAvatarClick() {
  if (!userStore.isAuthenticated) {
    const returnTo = route.fullPath
    // Nepřesm\u011brov\u00e1vat zp\u011bt na auth str\u00e1nky (jinak po loginu skon\u010d\u00ed zase na /register apod.)
    const isAuthPath = /^\/(login|register|password)(\/|$|\?)/.test(returnTo || '')
    window.location.href = returnTo && returnTo !== '/' && !isAuthPath
      ? `/login?returnTo=${encodeURIComponent(returnTo)}`
      : '/login'
    return
  }
  toggleMoreMenu()
}

function toggleTheme() {
  const isDark = theme.global.current.value.dark
  theme.global.name.value = isDark ? 'light' : 'customDarkTheme'
  localStorage.setItem('theme', theme.global.name.value)
}

// --- Language picker ---
const langOptions = [
  { code: 'cs' as AppLocale, label: 'Čeština', flag: '🇨🇿' },
  { code: 'en' as AppLocale, label: 'English', flag: '🇬🇧' },
  { code: 'de' as AppLocale, label: 'Deutsch', flag: '🇩🇪' },
]
const showLangPicker = ref(false)
const currentLocale = ref<AppLocale>(getCurrentLocale())
const currentLangLabel = computed(() => langOptions.find(l => l.code === currentLocale.value)?.label ?? 'Čeština')

function switchLocale(code: AppLocale) {
  setLocale(code)
  currentLocale.value = code
  showLangPicker.value = false
}

async function toggleMoreMenu() {
  if (activeMenu.value === 'more') {
    close()
    return
  }

  await nextTick()
  const btn = moreBtnRef.value
  if (btn) {
    menuPosition.value = computeMenuPosition(btn, { withMaxHeight: true })
  }

  activeMenu.value = 'more'
  document.documentElement.style.overflow = 'hidden'
  document.body.style.overflow = 'hidden'
  await nextTick()
  onNavScroll()
}

async function toggleSecondaryMenu() {
  if (activeMenu.value === 'secondary') {
    close()
    return
  }

  await nextTick()
  const btn = secondaryBtnRef.value
  if (btn) {
    secondaryMenuPosition.value = computeMenuPosition(btn)
  }

  activeMenu.value = 'secondary'
  document.documentElement.style.overflow = 'hidden'
  document.body.style.overflow = 'hidden'
}

function onCreateClick(e: MouseEvent) {
  // If createItems are defined, open context menu; otherwise call onCreate directly
  if (qaStore.createItems.length > 0) {
    toggleCreateMenu()
  } else {
    qaStore.onCreate?.(e)
  }
}

function openNewArticleWindow() {
  const path = route.path

  const leagueSeasonMatch = path.match(/^\/leagues\/([^/]+)\/([^/]+)\/articles$/)
  if (leagueSeasonMatch) {
    const leagueId = leagueSeasonMatch[1]
    const season = leagueSeasonMatch[2]
    router.push(`/leagues/${leagueId}/${season}/addarticle`)
    return
  }

  const academyMatch = path.match(/^\/leagues\/([^/]+)\/academy\/articles$/)
  if (academyMatch) {
    const leagueId = academyMatch[1]
    router.push(`/leagues/${leagueId}/academy/addarticle`)
    return
  }

  router.push('/newarticle')
}

function openEditArticleWindow() {
  const m = articleDetailMatch.value
  if (!m) return
  if (m.isAcademy) {
    router.push(`/leagues/${m.leagueId}/academy/addarticle/${m.urlTitle}`)
  } else {
    router.push(`/leagues/${m.leagueId}/${m.season}/addarticle/${m.urlTitle}`)
  }
}

function openArticleMailingModal() {
  if (!articleDetailMatch.value) return
  showArticleMailingModal.value = true
}

async function toggleCreateMenu() {
  if (activeMenu.value === 'create') {
    close()
    return
  }

  await nextTick()
  const btn = createBtnRef.value
  if (btn) {
    createMenuPosition.value = computeMenuPosition(btn)
  }

  activeMenu.value = 'create'
  document.documentElement.style.overflow = 'hidden'
  document.body.style.overflow = 'hidden'
}

async function toggleCalendarMenu() {
  if (activeMenu.value === 'calendar') {
    close()
    return
  }

  await nextTick()
  const btn = calendarBtnRef.value
  if (btn) {
    calendarMenuPosition.value = computeMenuPosition(btn)
  }

  activeMenu.value = 'calendar'
  document.documentElement.style.overflow = 'hidden'
  document.body.style.overflow = 'hidden'
}

const teamPencilItems = computed<QuickActionItem[]>(() => {
  const items: QuickActionItem[] = [
    { icon: 'mdi-account-group-outline', label: t('nav.members'), action: 'teamMembers' },
  ]
  if (isTeamAdmin.value || isTeamMember.value) {
    items.push({ icon: 'mdi-cash-register', label: t('nav.priceList'), action: 'teamPriceList' })
  }
  if (isTeamAdmin.value) {
    items.push({ icon: 'mdi-cog-outline', label: t('nav.edit'), action: 'teamEdit' })
  }
  return items
})

function close() {
  activeMenu.value = null
  showLangPicker.value = false
  secondaryView.value = 'main'
  document.documentElement.style.overflow = ''
  document.body.style.overflow = ''
}

function onItemClick(item: QuickActionItem) {
  // Disabled report for unauthenticated users — keep menu open
  if (item.action === 'report' && !userStore.isAuthenticated) return

  // Drill-down navigation inside the secondary 3-dot menu — do NOT close
  if (item.action === 'submenu:more') {
    secondaryView.value = 'more'
    return
  }
  if (item.action === 'submenu:back') {
    secondaryView.value = 'main'
    return
  }

  close()

  // Handle hardcoded nav actions directly — works on every route
  switch (item.action) {
    case 'profile':
      router.push(`/users/${userStore.user?._id}`)
      return
    case 'account':
      router.push('/account')
      return
    case 'messages':
      router.push('/messages')
      return
    case 'players':
      router.push('/players')
      return
    case 'dependants':
      router.push('/dependants')
      return
    case 'createInvitation':
      router.push('/invite')
      return
    case 'contact':
      window.open('https://www.hockeybook.online/contact', '_blank', 'noopener,noreferrer')
      return
    case 'report':
      if (!userStore.isAuthenticated) return
      if (isMessagesConvRoute.value) {
        openReportPickerForMessages()
      } else {
        showReportModal.value = true
      }
      return
    case 'blockUser':
      openBlockModalForCurrentRoute()
      return
    case 'blockFromMessages':
      openBlockFromMessages()
      return
    case 'userSendMessage':
      qaStore.onHighlights?.()
      return
    case 'bugReport':
      router.push('/bug-report')
      return
    case 'createEvent':
      // Prefer page-level handler; fallback to global styled EventFormModal
      if (qaStore.onAction) {
        qaStore.onAction('createEvent')
      } else {
        globalEventInitialDate.value = new Date()
        showGlobalEventFormModal.value = true
      }
      return
    case 'logout':
      localStorage.removeItem('token')
      userStore.clearUser()
      window.location.href = '/login'
      return
  }

  // Team pencil menu actions
  switch (item.action) {
    case 'teamMembers':
      showMembersModal.value = true
      return
    case 'teamPriceList':
      showPriceListModal.value = true
      return
    case 'teamEdit':
      showTeamFormModal.value = true
      return
  }

  // League admin actions
  if (item.action === 'leagueSettings') {
    showLeagueFormModal.value = true
    return
  }

  if (item.action === 'simulateMatches') {
    showSimulateMatchesConfirm.value = true
    return
  }

  if (item.action === 'closeRegularSeason') {
    showCloseRegularConfirm.value = true
    return
  }

  if (item.action === 'closePlayoffGroups') {
    showClosePlayoffGroupsConfirm.value = true
    return
  }

  // Team admin actions (from 3-dot menu)
  if (item.action === 'teamSettings') {
    showTeamFormModal.value = true
    return
  }

  // Leave team (from 3-dot "More" submenu)
  if (item.action === 'leaveTeam') {
    showLeaveTeamConfirm.value = true
    return
  }

  if (item.action === 'leaveGroup') {
    showLeaveGroupConfirm.value = true
    return
  }

  // Academy actions
  if (item.action === 'academyEditLeague') {
    showLeagueFormModal.value = true
    return
  }

  // League navigation actions
  if (item.action.startsWith('leagueNav:')) {
    router.push(item.action.replace('leagueNav:', ''))
    return
  }

  // Everything else — call page callback
  qaStore.onAction?.(item.action)
}
</script>

<style lang="scss" scoped>
/* ── Back button enter/leave animation ── */
.back-btn-enter-active {
  transition: opacity 0.35s ease, transform 0.4s cubic-bezier(0.34, 1.4, 0.64, 1);
}

.back-btn-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.back-btn-enter-from {
  opacity: 0;
  transform: scale(0);
}

.back-btn-leave-to {
  opacity: 0;
  transform: scale(0.7);
}

/* Standalone back button on the left */
.back-button-container {
  position: fixed;
  z-index: 9000;
  /* top/left transition is for position changes (banner show/hide).
     opacity & transform are handled by Vue Transition (.back-btn-*) classes. */
  transition: top 0.3s cubic-bezier(0.4, 0, 0.2, 1), left 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &.qa-hidden {
    opacity: 0;
    pointer-events: none;
    transition: top 0.3s cubic-bezier(0.4, 0, 0.2, 1), left 0.3s cubic-bezier(0.4, 0, 0.2, 1),
      opacity 0.15s ease;
  }

  /* Ensure Vue Transition classes win over the base transition */
  &.back-btn-enter-active {
    transition: opacity 0.35s ease, transform 0.4s cubic-bezier(0.34, 1.4, 0.64, 1);
  }

  &.back-btn-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
  }
}

.back-button-pill {
  display: flex;
  align-items: center;
  backdrop-filter: blur(7px);
  -webkit-backdrop-filter: blur(7px);
  border-radius: 50px;
  padding: 4px;

  /* Dark mode (default) */
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.25),
    inset 0 -1px 0 rgba(0, 0, 0, 0.1),
    0 4px 20px rgba(0, 0, 0, 0.15);

  .is-light & {
    background: rgba(255, 255, 255, 0.35);
    border: 1px solid rgba(0, 0, 0, 0.12);
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.6),
      inset 0 -1px 0 rgba(0, 0, 0, 0.04),
      0 4px 20px rgba(0, 0, 0, 0.08);
  }
}

.back-button-standalone {
  width: 42px;
  height: 42px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
  color: rgba(var(--v-theme-sheetText), 0.9);
  -webkit-tap-highlight-color: transparent;

  &:active {
    transform: scale(0.9);
    background: rgba(var(--v-theme-sheetText), 0.1);
  }
}

.quick-actions-container {
  position: fixed;
  z-index: 9000;
  transition: top 0.3s cubic-bezier(0.4, 0, 0.2, 1), right 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.15s ease;

  &.qa-hidden {
    opacity: 0;
    pointer-events: none;
  }
}

/* ── Pill width/layout animation ── */
.qa-pill-animate {
  transition: padding 0.35s cubic-bezier(0.4, 0, 0.2, 1),
    border-radius 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  /* wrap: let browser calculate intrinsic width for animation via max-content */
  width: max-content;
}

/* Slot wrapper for animated button groups */
.qa-slot {
  display: flex;
  align-items: center;
  overflow: hidden;
}

/* ── qa-slot transition: expand → fade in / fade out → shrink ── */
/* Enter: first expand width over 0.25s, then fade in icons with 0.15s delay */
.qa-slot-enter-active {
  transition: max-width 0.25s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.2s ease 0.15s;
}

/* Leave: first fade out icons, then shrink width with 0.12s delay */
.qa-slot-leave-active {
  transition: opacity 0.15s ease,
    max-width 0.25s cubic-bezier(0.4, 0, 0.2, 1) 0.1s;
}

.qa-slot-enter-from {
  max-width: 0;
  opacity: 0;
}

.qa-slot-enter-to {
  max-width: 100px;
  opacity: 1;
}

.qa-slot-leave-from {
  max-width: 100px;
  opacity: 1;
}

.qa-slot-leave-to {
  max-width: 0;
  opacity: 0;
}

/* Animated children inside the pill */
.qa-pill-animate>.action-button,
.qa-pill-animate>.button-divider {
  transition: opacity 0.25s ease, transform 0.25s ease, width 0.25s ease;
}

/* First visible slot always gets left-rounded to match pill edge */
.combined-buttons>.qa-slot:first-child .action-button {
  border-radius: 50% 8px 8px 50% !important;
}

.combined-buttons {
  display: flex;
  align-items: center;
  backdrop-filter: blur(7px);
  -webkit-backdrop-filter: blur(7px);
  border-radius: 50px;
  padding: 4px;
  max-width: calc(100vw - 20px);

  &.avatar-only {
    border-radius: 50%;
    padding: 4px;
  }

  &.avatar-only .right-button {
    border-radius: 50%;
  }

  /* Dark mode (default) */

  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.25),
  inset 0 -1px 0 rgba(0, 0, 0, 0.1),
  0 4px 20px rgba(0, 0, 0, 0.15);

  /* Light mode */
  .is-light & {
    background: rgba(255, 255, 255, 0.35);
    border: 1px solid rgba(0, 0, 0, 0.12);
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.6),
      inset 0 -1px 0 rgba(0, 0, 0, 0.04),
      0 4px 20px rgba(0, 0, 0, 0.08);
  }
}

.action-button {
  width: 42px;
  height: 42px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: rgba(var(--v-theme-sheetText), 0.9);
  -webkit-tap-highlight-color: transparent;

  &:active:not(:disabled) {
    transform: scale(0.9);
    background: rgba(var(--v-theme-sheetText), 0.1);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    pointer-events: none;
  }

  &.active {
    background: rgba(var(--v-theme-sheetText), 0.12);
  }

  &.left-button {
    border-radius: 50% 8px 8px 50%;
  }

  &.middle-button {
    border-radius: 8px;
    position: relative;
  }

  &.filter-active {
    color: rgb(var(--v-theme-primary));
  }

  &.right-button {
    border-radius: 8px 50% 50% 8px;
  }

  &.avatar-button {
    padding: 0;
  }
}

.filter-badge {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgb(var(--v-theme-primary));
}

.chat-unread-badge :deep(.v-badge__badge) {
  min-width: 10px;
  height: 10px;
  border: 2px solid black;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.3);
}

.avatar-initials {
  font-size: 13px;
  font-weight: 600;
  color: white;
  line-height: 1;
}

.login-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 5px 5px 14px;
  border: none;
  background: transparent;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  &:active {
    transform: scale(0.95);
  }
}

.login-label {
  font-size: 14px;
  font-weight: 600;
  color: white;
  white-space: nowrap;
}

.is-light .login-label {
  color: #333;
}

.button-divider {
  width: 1px;
  height: 22px;
  background: linear-gradient(180deg,
      transparent 0%,
      rgba(var(--v-theme-sheetText), 0.15) 50%,
      transparent 100%);
}

// ── Simulate confirm dialog (reusing delete-confirm pattern) ──
.delete-confirm-dialog {
  padding: 24px;
  text-align: center;
}

.delete-confirm-icon {
  margin-bottom: 16px;
}

.delete-confirm-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.delete-confirm-subtitle {
  font-size: 0.9rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
  margin-bottom: 16px;
}

.delete-confirm-input-wrap {
  margin-bottom: 20px;
}

.delete-confirm-input {
  width: 100%;
  padding: 10px 14px;
  border-radius: 10px;
  border: 1.5px solid rgba(var(--v-theme-on-surface), 0.15);
  font-size: 0.95rem;
  text-align: center;
  background: transparent;
  color: inherit;
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: #2196f3;
  }
}

.delete-confirm-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.delete-confirm-btn {
  padding: 10px 24px;
  border-radius: 12px;
  border: none;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &--cancel {
    background: rgba(var(--v-theme-on-surface), 0.1);
    color: inherit;
  }

  &--confirm {
    background: #2196f3;
    color: white;

    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
  }

  &:active:not(:disabled) {
    transform: scale(0.96);
  }
}

.conflicts-list {
  width: 100%;
  margin: 8px 0 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.conflict-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 8px;
  background: rgba(var(--v-theme-on-surface), 0.05);
  font-size: 13px;
}

.conflict-position {
  font-weight: 700;
  min-width: 36px;
}

.conflict-old {
  color: rgba(var(--v-theme-error), 1);
  text-decoration: line-through;
}

.conflict-arrow {
  opacity: 0.4;
}

.conflict-new {
  font-weight: 600;
  color: rgba(var(--v-theme-success), 1);
}

.override-list {
  width: 100%;
  margin: 8px 0 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.override-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 10px;
  border-radius: 8px;
  background: rgba(var(--v-theme-on-surface), 0.05);
  font-size: 13px;
  text-align: left;
}

.override-location {
  font-size: 11px;
  opacity: 0.5;
}

.override-change {
  display: flex;
  align-items: center;
  gap: 6px;
}

.override-arrow {
  opacity: 0.4;
}
</style>

<style lang="scss">
/* Global (not scoped) — Teleported elements */
.quick-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: transparent;
  z-index: 9990;
}

/* ─── Nav Context Menu (dropdown) ─── */
.nav-context-menu {
  min-width: 300px;
  max-width: 360px;
  width: 340px;
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  overflow: hidden;
}

/* User card */
.nav-user-card {
  padding: 22px 20px 16px;
  border-bottom: 1px solid rgba(var(--v-theme-sheetText), 0.07);
  background: rgba(var(--v-theme-sheetText), 0.03);
}

.nav-user-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.nav-user-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(var(--v-theme-sheetText), 0.08);
  border: 2px solid rgba(var(--v-theme-primary), 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 18px;
  color: rgb(var(--v-theme-primary));
  flex-shrink: 0;
  overflow: hidden;
}

.nav-user-info {
  flex: 1;
  min-width: 0;
}

.nav-user-name {
  font-size: 16px;
  font-weight: 700;
  color: rgb(var(--v-theme-sheetText));
}

.nav-user-meta {
  font-size: 12px;
  color: rgba(var(--v-theme-sheetText), 0.45);
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nav-user-badge {
  margin-left: auto;
  padding: 3px 10px;
  background: rgba(var(--v-theme-primary), 0.1);
  border: 1px solid rgba(var(--v-theme-primary), 0.2);
  border-radius: 100px;
  font-size: 11px;
  font-weight: 700;
  color: rgb(var(--v-theme-primary));
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex-shrink: 0;
}

.nav-quick-actions {
  display: flex;
  gap: 6px;
  padding-top: 12px;
}

.nav-quick-btn {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  padding: 10px 6px;
  border-radius: 12px;
  background: rgba(var(--v-theme-sheetText), 0.05);
  border: 1px solid rgba(var(--v-theme-sheetText), 0.07);
  cursor: pointer;
  transition: all 0.2s;
  font-size: 11px;
  color: rgba(var(--v-theme-sheetText), 0.65);
  font-weight: 600;
  -webkit-tap-highlight-color: transparent;

  &:active {
    transform: scale(0.96);
  }

  &:hover {
    background: rgba(var(--v-theme-sheetText), 0.08);
    color: rgb(var(--v-theme-sheetText));
  }

  &.accent {
    background: rgba(var(--v-theme-primary), 0.1);
    border-color: rgba(var(--v-theme-primary), 0.2);
    color: rgb(var(--v-theme-primary));

    &:hover {
      background: rgba(var(--v-theme-primary), 0.2);
    }
  }
}

.nav-badge {
  background: rgb(var(--v-theme-error));
  border-radius: 6px;
  font-size: 9px;
  font-weight: 800;
  padding: 1px 5px;
  color: white;
  line-height: 1.2;
}

/* Main routes navigation */
.nav-main-routes {
  display: flex;
  padding: 2px 12px 0;
  margin-bottom: 2px;
  border-bottom: 1px solid rgba(var(--v-theme-sheetText), 0.06);
}

.nav-route-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 8px 4px 10px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  transition: all 0.15s;
  font-size: 12px;
  font-weight: 600;
  color: rgba(var(--v-theme-sheetText), 0.4);
  -webkit-tap-highlight-color: transparent;
  margin-bottom: -1px;

  &:active {
    opacity: 0.7;
  }

  &:hover {
    color: rgba(var(--v-theme-sheetText), 0.7);
  }

  &.active {
    color: rgb(var(--v-theme-primary));
    border-bottom-color: rgb(var(--v-theme-primary));
  }
}

/* Scroll area */
.nav-scroll-wrapper {
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.nav-scroll {
  flex: 1;
  overflow-y: auto;
  scrollbar-width: none;
  padding: 8px 12px;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
}

.nav-scroll-fade {
  position: absolute;
  left: 0;
  right: 0;
  height: 100px;
  pointer-events: none;
  z-index: 2;
  opacity: 0;
  transition: opacity 0.2s;

  &.visible {
    opacity: 1;
  }

  &--top {
    top: 0;
    background: linear-gradient(to bottom, #161E30 0%, transparent 100%);

    body:has(.v-theme--light) & {
      background: linear-gradient(to bottom, #ffffff 0%, transparent 100%);
    }
  }

  &--bottom {
    bottom: 0;
    background: linear-gradient(to top, #161E30 0%, transparent 100%);

    body:has(.v-theme--light) & {
      background: linear-gradient(to top, #ffffff 0%, transparent 100%);
    }
  }
}

.nav-section-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: rgba(var(--v-theme-sheetText), 0.35);
  padding: 8px 8px 4px;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  color: rgba(var(--v-theme-sheetText), 0.65);
  cursor: pointer;
  transition: all 0.15s;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  border: 1px solid transparent;

  &:hover {
    background: rgba(var(--v-theme-sheetText), 0.07);
    color: rgb(var(--v-theme-sheetText));
  }

  &:active {
    transform: scale(0.98);
  }

  &.active {
    background: rgba(var(--v-theme-primary), 0.1);
    color: rgb(var(--v-theme-primary));
    border-color: rgba(var(--v-theme-primary), 0.18);
  }
}

.nav-item-icon {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: rgba(var(--v-theme-sheetText), 0.04);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  .active & {
    background: rgba(var(--v-theme-primary), 0.1);
  }
}

.nav-item-toggle {
  align-items: center;
}

/* Language picker */
.nav-item-lang {
  cursor: pointer;
  align-items: center;
}

.lang-options {
  display: flex;
  flex-direction: column;
  padding: 0 4px 4px;
  overflow: hidden;
}

.lang-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: none;
  background: none;
  color: rgba(var(--v-theme-sheetText), 0.75);
  font-size: 13px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: rgba(var(--v-theme-sheetText), 0.07);
  }

  &.active {
    color: rgb(var(--v-theme-primary));
    font-weight: 600;
  }
}

.lang-flag {
  font-size: 16px;
  line-height: 1;
}

.lang-expand-enter-active,
.lang-expand-leave-active {
  transition: all 0.2s ease;
  max-height: 250px;
}

.lang-expand-enter-from,
.lang-expand-leave-to {
  max-height: 0;
  opacity: 0;
}

/* Theme toggle inside nav */
.nav-toggle {
  margin-left: auto;
  width: 36px;
  height: 20px;
  background: rgba(var(--v-theme-sheetText), 0.15);
  border-radius: 100px;
  position: relative;
  transition: all 0.3s;

  &.on {
    background: rgb(var(--v-theme-primary));
  }
}

.nav-toggle-knob {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  background: rgb(var(--v-theme-sheetText));
  border-radius: 50%;
  transition: all 0.3s;

  .on & {
    left: 18px;
    background: rgb(var(--v-theme-sheetSection));
  }
}

/* Footer */
.nav-footer {
  padding: 10px épx;
  border-top: 1px solid rgba(var(--v-theme-sheetText), 0.07);
  display: flex;
  gap: 6px;
}

.nav-version {
  text-align: center;
  font-size: 11px;
  color: rgba(var(--v-theme-sheetText), 0.25);
  padding: 0;
}

.nav-footer-btn {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 6px;
  border-radius: 10px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  color: rgba(var(--v-theme-sheetText), 0.65);
  background: transparent;
  border: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-tap-highlight-color: transparent;

  .v-icon {
    flex-shrink: 0;
  }

  &:hover {
    background: rgba(var(--v-theme-sheetText), 0.07);
    color: rgb(var(--v-theme-sheetText));
  }

  &:active {
    transform: scale(0.96);
  }

  &.logout {
    color: rgb(var(--v-theme-error));

    &:hover {
      background: rgba(var(--v-theme-error), 0.1);
    }
  }

  &.report {
    color: #f97316;

    &:hover {
      background: rgba(249, 115, 22, 0.1);
      color: #fb923c;
    }
  }
}

/* ─── Context Menu (Secondary) ─── */
.quick-context-menu {
  position: fixed;
  min-width: 220px;
  max-width: 260px;
  max-height: calc(100vh - 24px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background: #161E30;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 16px;
  box-shadow:
    0 24px 64px rgba(0, 0, 0, 0.6),
    0 8px 24px rgba(0, 0, 0, 0.4),
    0 -1px 0 0 rgba(255, 255, 255, 0.06) inset;
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  padding: 4px;
  z-index: 9995;
}

/* Light mode override */
body:has(.v-theme--light) .quick-context-menu {
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow:
    0 24px 64px rgba(0, 0, 0, 0.12),
    0 8px 24px rgba(0, 0, 0, 0.06),
    0 -1px 0 0 rgba(255, 255, 255, 0.8) inset;
}

.quick-context-menu-content {
  padding: 0;
}

.quick-menu-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
  color: rgba(255, 255, 255, 0.65);
  font-size: 14px;
  font-weight: 600;
  user-select: none;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    background: rgba(255, 255, 255, 0.07);
    color: #ffffff;

    .v-icon {
      color: #ffffff !important;
      opacity: 1;
    }
  }

  &:active {
    background: rgba(255, 255, 255, 0.04);
    transform: scale(0.98);
  }

  .v-icon {
    color: rgba(255, 255, 255, 0.35) !important;
    transition: color 0.15s ease, opacity 0.15s ease;
    flex-shrink: 0;
  }

  &.quick-menu-item-active {
    color: rgb(var(--v-theme-primary));
    background: rgba(var(--v-theme-primary), 0.08);
  }

  &.quick-menu-item--disabled {
    cursor: not-allowed;
    opacity: 0.55;

    &:hover {
      background: transparent;
      color: rgba(255, 255, 255, 0.65);

      .v-icon {
        color: rgba(255, 255, 255, 0.35) !important;
        opacity: 1;
      }
    }

    &:active {
      transform: none;
      background: transparent;
    }
  }

  .quick-menu-item-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    flex: 1;
  }

  .quick-menu-item-sub {
    font-size: 11px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.45);
    line-height: 1.25;
  }

  span {
    font-size: 14px;
    font-weight: 600;
  }
}

/* Light mode override for items */
body:has(.v-theme--light) .quick-menu-item {
  color: rgba(0, 0, 0, 0.7);

  .v-icon {
    color: rgba(0, 0, 0, 0.4) !important;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.06);
    color: #000000;

    .v-icon {
      color: #000000 !important;
    }
  }

  &:active {
    background: rgba(0, 0, 0, 0.04);
  }

  .quick-menu-item-sub {
    color: rgba(0, 0, 0, 0.5);
  }

  &.quick-menu-item--disabled:hover {
    background: transparent;
    color: rgba(0, 0, 0, 0.7);

    .v-icon {
      color: rgba(0, 0, 0, 0.4) !important;
    }
  }
}

.quick-menu-divider {
  height: 1px;
  background: linear-gradient(90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.08) 20%,
      rgba(255, 255, 255, 0.08) 80%,
      transparent 100%);
  margin: 3px 0;
}

.quick-menu-header {
  padding: 8px 12px 4px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(255, 255, 255, 0.35);
  user-select: none;

  &:not(:first-child) {
    margin-top: 4px;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    padding-top: 10px;
  }
}

body:has(.v-theme--light) .quick-menu-divider {
  background: linear-gradient(90deg,
      transparent 0%,
      rgba(0, 0, 0, 0.08) 20%,
      rgba(0, 0, 0, 0.08) 80%,
      transparent 100%);
}

body:has(.v-theme--light) .quick-menu-header {
  color: rgba(0, 0, 0, 0.4);

  &:not(:first-child) {
    border-top-color: rgba(0, 0, 0, 0.06);
  }
}

/* Transition */
.quick-menu-enter-active {
  transition: all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.quick-menu-leave-active {
  transition: all 0.18s ease;
}

.quick-menu-enter-from {
  opacity: 0;
  transform: translateY(16px) scale(0.92);
}

.quick-menu-leave-to {
  opacity: 0;
  transform: translateY(8px) scale(0.96);
}
</style>
